import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCMS } from '../context/CMSContext';
import type { CMSData, NewsItem, Inquiry, MediaFile, UserRole } from '../types/cms';
import { LogOut, Save, Plus, Trash2, Image as ImageIcon, KeyRound, Sun, Moon, Loader2 } from 'lucide-react';
import HandoverPanel from '../components/admin/HandoverPanel';
import { smartCompressImage } from '../lib/imageCompression';

// 社群推廣預設標籤庫（與 apps/cms-worker default.ts 同步；CMS 尚未設定 socialPromotion 時的預填值）
const DEFAULT_HASHTAG_GROUPS: { name: string; tags: string[] }[] = [
  { name: '地區', tags: ['#荃灣', '#葵芳', '#葵興', '#荔枝角', '#地鐵'] },
  { name: '類型', tags: ['#工廈', '#商廈', '#工商廈', '#地產', '#辦公室', '#寫字樓', '#倉', '#食物工場', '#24工作室'] },
  { name: '租售', tags: ['#售', '#租', '#交吉'] },
  { name: '特色', tags: ['#荀盤', '#四正無柱', '#間隔實用', '#高樓底', '#新裝修', '#內廁', '#有貨台', '#原裝', '#冷凍倉', '#大雪櫃', '#可入櫃', '#半倉', '#適合各行各業', '#創業'] },
];
import { applyDiagonalWatermark, renderWatermarkPreview, DEFAULT_WATERMARK_OPACITY } from '../lib/imageWatermark';
import { PropertyManager } from '../components/admin/PropertyManager';
import { MediaPicker } from '../components/admin/MediaPicker';
import { MediaLibrary } from '../components/admin/MediaLibrary';
import { AnalyticsDashboard } from '../components/admin/AnalyticsDashboard';
import { TeamManager } from '../components/admin/TeamManager';
import { AuditLog } from '../components/admin/AuditLog';

const apiBase = import.meta.env.VITE_CMS_API_URL || 'https://marveland-cms.jimsbond007.workers.dev';

const TextInput = ({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; type?: string }) => (
  <div className="mb-4">
    <label className="mb-1 block text-sm font-semibold text-slate-700">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none"
    />
  </div>
);

const TextArea = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
  <div className="mb-4">
    <label className="mb-1 block text-sm font-semibold text-slate-700">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={4}
      className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none"
    />
  </div>
);

const ImageInput = ({
  label,
  value,
  onChange,
  onPick,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onPick: () => void;
}) => (
  <div className="mb-4">
    <label className="mb-1 block text-sm font-semibold text-slate-700">{label}</label>
    <div className="flex gap-2">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm focus:border-gold-600 focus:outline-none"
      />
      <button
        type="button"
        onClick={onPick}
        className="flex shrink-0 items-center gap-1.5 rounded bg-blue-800 px-3 py-2 text-xs font-bold text-white hover:bg-blue-900"
      >
        <ImageIcon className="h-4 w-4" />
        從媒體庫選擇
      </button>
    </div>
  </div>
);

const Admin: React.FC = () => {
  const { cmsData, refresh } = useCMS();
  const [token, setToken] = useState<string | null>(localStorage.getItem('marveland_admin_token'));
  const [role, setRole] = useState<UserRole | null>(localStorage.getItem('marveland_admin_role') as UserRole | null);
  const [currentUsername, setCurrentUsername] = useState<string>(localStorage.getItem('marveland_admin_username') || '');
  const [currentUserId, setCurrentUserId] = useState<string>(localStorage.getItem('marveland_admin_userid') || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState<'content' | 'properties' | 'team' | 'news' | 'inquiries' | 'media' | 'analytics' | 'audit' | 'handover'>('properties');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  // 後台暗黑模式（僅 /admin 生效；離開頁面自動還原前台亮色）
  const [adminDark, setAdminDark] = useState(() => {
    try { return localStorage.getItem('admin_theme') === 'dark'; } catch { return false; }
  });
  const [draft, setDraft] = useState<CMSData>(cmsData);
  const [isDirty, setIsDirty] = useState(false);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [media, setMedia] = useState<MediaFile[]>([]);
  const [mediaUsage, setMediaUsage] = useState(0);
  const [contentImagePickerOpen, setContentImagePickerOpen] = useState(false);
  const [contentImagePickerTarget, setContentImagePickerTarget] = useState<
    'logo' | 'hero' | 'about' | { type: 'news'; index: number } | null
  >(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const isAdmin = role === 'admin';

  const usedMediaIds = useMemo(() => {
    const ids = new Set<string>();
    const addUrl = (url: string | undefined) => {
      if (!url) return;
      const match = url.match(/\/api\/media\/([^/]+)$/);
      if (match) ids.add(match[1]);
    };
    draft.properties.forEach((p) => {
      p.images.forEach(addUrl);
      addUrl(p.floorPlan);
    });
    addUrl(draft.siteConfig.logo);
    addUrl(draft.siteConfig.favicon);
    addUrl(draft.hero.backgroundImage);
    addUrl(draft.about.image);
    draft.news.forEach((n) => addUrl(n.image));
    draft.agents.forEach((a) => addUrl(a.photo));
    return ids;
  }, [draft]);

  useEffect(() => {
    // 僅 /admin 掛 .dark；離開（unmount）時移除，前台不受影響
    document.documentElement.classList.toggle('dark', adminDark);
    return () => document.documentElement.classList.remove('dark');
  }, [adminDark]);

  const toggleAdminDark = () => {
    const next = !adminDark;
    setAdminDark(next);
    try { localStorage.setItem('admin_theme', next ? 'dark' : 'light'); } catch { /* 儲存失敗不影響本次切換 */ }
  };

  // 標籤庫輸入草稿：打字期間原樣顯示（否則逗號會被即時正規化吃掉），Enter/失焦才提交
  const [tagInputDrafts, setTagInputDrafts] = useState<Record<number, string>>({});
  // FB 短連結體檢
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditResult, setAuditResult] = useState<{ total: number; aliveCount: number; deadCount: number; dead: { code: string; shortUrl: string; propertyId: string; clicks: number }[] } | null>(null);
  const commitTagDraft = (gi: number) => {
    const raw = tagInputDrafts[gi];
    if (raw === undefined) return;
    const groups = [...(draft.siteConfig.socialPromotion?.hashtagGroups || [])];
    const seen = new Set<string>();
    const tags = raw
      .split(/[，,]/)
      .map((t) => {
        const n = t.trim().replace(/^＃+/, '').replace(/^#+/, '').replace(/\s+/g, '');
        return n ? `#${n}` : '';
      })
      .filter((t) => {
        if (!t || seen.has(t)) return false;
        seen.add(t);
        return true;
      });
    groups[gi] = { ...groups[gi], tags };
    setDraft({ ...draft, siteConfig: { ...draft.siteConfig, socialPromotion: { hashtagGroups: groups } } });
    setTagInputDrafts((prev) => {
      const next = { ...prev };
      delete next[gi];
      return next;
    });
  };

  // 防盜水印濃度（Admin 可調；存 siteConfig.watermark.opacity；僅影響之後新上傳的照片）
  const watermarkOpacity = draft.siteConfig.watermark?.opacity ?? DEFAULT_WATERMARK_OPACITY;
  const wmPreviewRef = useRef<HTMLCanvasElement | null>(null);
  const [wmSampleImg, setWmSampleImg] = useState<HTMLImageElement | null>(null);
  const wmSampleUrlRef = useRef<string | null>(null);
  // 每次渲染都重畫（成本低）：修復「從其他分頁切過來時畫布空白」
  useEffect(() => {
    if (wmPreviewRef.current) renderWatermarkPreview(wmPreviewRef.current, watermarkOpacity, wmSampleImg);
  });

  // 預設樣本：用網站 Hero 背景照片（CMS 動態取，無則顯示漸層樣本）
  useEffect(() => {
    if (wmSampleImg) return;
    const src = draft.hero.backgroundImage;
    if (!src) return;
    const img = new Image();
    img.onload = () => setWmSampleImg((prev) => prev ?? img);
    img.src = src;
  }, [draft.hero.backgroundImage, wmSampleImg]);

  const handleWmSamplePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => {
      if (wmSampleUrlRef.current) URL.revokeObjectURL(wmSampleUrlRef.current);
      wmSampleUrlRef.current = url;
      setWmSampleImg(img);
    };
    img.src = url;
  };

  const handleWmSampleReset = () => {
    if (wmSampleUrlRef.current) {
      URL.revokeObjectURL(wmSampleUrlRef.current);
      wmSampleUrlRef.current = null;
    }
    setWmSampleImg(null);
  };

  useEffect(() => {
    // 既有 KV 資料可能尚無 socialPromotion：預填預設標籤庫，客人儲存內容後即固化（未儲存不影響前端 fallback）
    if (!cmsData.siteConfig.socialPromotion?.hashtagGroups?.length) {
      setDraft({ ...cmsData, siteConfig: { ...cmsData.siteConfig, socialPromotion: { hashtagGroups: DEFAULT_HASHTAG_GROUPS } } });
    } else {
      setDraft(cmsData);
    }
  }, [cmsData]);

  // 當 draft 與後端同步後的 cmsData 一致時，視為沒有未儲存變更
  useEffect(() => {
    setIsDirty(JSON.stringify(draft) !== JSON.stringify(cmsData));
  }, [draft, cmsData]);

  useEffect(() => {
    if (!token) return;
    if (activeTab === 'inquiries') fetchInquiries();
    if (activeTab === 'media') fetchMedia();
  }, [activeTab, token]);

  useEffect(() => {
    if (!token) return;
    fetchMedia();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetch(`${apiBase}/api/admin/session`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res.status === 401) {
          handleUnauthorized();
        }
      })
      .catch(() => {
        // network error, keep current session and let next request fail visibly
      });
  }, [token]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`${apiBase}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('marveland_admin_token', data.token);
        localStorage.setItem('marveland_admin_role', data.role);
        localStorage.setItem('marveland_admin_username', data.username);
        localStorage.setItem('marveland_admin_userid', data.userId);
        setToken(data.token);
        setCurrentUserId(data.userId);
        setRole(data.role);
        setCurrentUsername(data.username);
        setActiveTab(data.role === 'staff' ? 'properties' : 'content');
      } else {
        setLoginError(data.error || '登入失敗');
      }
    } catch (err) {
      setLoginError(String(err));
    }
  };

  const logout = async () => {
    if (token) {
      try {
        await fetch(`${apiBase}/api/admin/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // non-blocking
      }
    }
    localStorage.removeItem('marveland_admin_token');
    localStorage.removeItem('marveland_admin_role');
    localStorage.removeItem('marveland_admin_username');
    localStorage.removeItem('marveland_admin_userid');
    setToken(null);
    setRole(null);
    setCurrentUsername('');
    setCurrentUserId('');
  };

  const audit = async (action: string, resource?: string, resourceId?: string, details?: Record<string, any>) => {
    if (!token) return;
    try {
      await fetch(`${apiBase}/api/admin/audit-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action, resource, resourceId, details }),
      });
    } catch {
      // non-blocking
    }
  };

  const showMessage = (msg: string, type: 'success' | 'error' = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleUnauthorized = async () => {
    showMessage('登入已過期，請重新登入', 'error');
    await logout();
  };

  const saveDraft = async (dataToSave = draft): Promise<boolean> => {
    setSaving(true);
    try {
      // properties 一律走單筆端點（/api/admin/properties*）伺服器端 read-modify-write；
      // 整包儲存若夾帶舊的 properties 陣列，會把同事剛存的物業變更回滾，故剝離不送
      const { properties: _strippedProperties, ...payload } = dataToSave;
      const res = await fetch(`${apiBase}/api/cms/data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        await refresh();
        showMessage('儲存成功');
        await audit('cms_update');
        return true;
      } else if (res.status === 401 || res.status === 403) {
        showMessage('權限不足或登入已過期，請重新登入', 'error');
        logout();
        return false;
      } else {
        showMessage('儲存失敗', 'error');
        return false;
      }
    } catch (err) {
      showMessage(String(err), 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleChangeOwnPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !currentUserId) return;
    try {
      const res = await fetch(`${apiBase}/api/admin/users/${currentUserId}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (res.status === 401) {
        await handleUnauthorized();
        return;
      }
      const data = await res.json();
      if (data.success) {
        showMessage('密碼已更新');
        setPasswordModalOpen(false);
        setCurrentPassword('');
        setNewPassword('');
        await audit('user_password_change', 'user', currentUserId, { selfService: true });
      } else {
        showMessage(`更新失敗：${data.error}`, 'error');
      }
    } catch (err) {
      showMessage(String(err), 'error');
    }
  };

  const fetchInquiries = async () => {
    const res = await fetch(`${apiBase}/api/inquiries`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
      await handleUnauthorized();
      return;
    }
    if (res.ok) setInquiries(await res.json());
  };

  const fetchMedia = async () => {
    // 2026-08-27：分頁抓全部（R2 化後已無巨型 base64，一次載入全量沒有壓力）——
    // 舊版未帶 page/limit 參數，伺服器預設只回第 1 頁 20 筆，導致媒體庫「只剩 20 張」的誤會
    try {
      const all: MediaFile[] = [];
      let usage = 0;
      let page = 1;
      for (;;) {
        const res = await fetch(`${apiBase}/api/media?page=${page}&limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          await handleUnauthorized();
          return;
        }
        if (!res.ok) {
          showMessage(`媒體庫載入失敗（HTTP ${res.status}）`, 'error');
          return;
        }
        const data = await res.json();
        all.push(...(data.media || []));
        usage = data.usage ?? usage;
        const totalPages = data.pagination?.totalPages ?? page;
        if (page >= totalPages) break;
        page++;
      }
      setMedia(all);
      setMediaUsage(usage);
    } catch (e) {
      showMessage(`媒體庫載入失敗：${e instanceof Error ? e.message : String(e)}`, 'error');
    }
  };

  const handleUploadFile = async (
    file: File,
    meta?: { category?: string; tags?: string[] }
  ): Promise<string | undefined> => {
    let compressed: Awaited<ReturnType<typeof smartCompressImage>> | undefined;
    try {
      // 防盜水印（中央對角 MARVELAND）先於壓縮套用——濃度由後台「防盜水印濃度」設定
      const marked = await applyDiagonalWatermark(file, watermarkOpacity);
      compressed = await smartCompressImage(marked);
    } catch (compressErr) {
      showMessage(`圖片處理失敗：${compressErr instanceof Error ? compressErr.message : String(compressErr)}`, 'error');
      throw compressErr;
    }

    const formData = new FormData();
    formData.append('file', compressed.file);
    if (meta?.category) formData.append('category', meta.category);
    if (meta?.tags?.length) formData.append('tags', JSON.stringify(meta.tags));
    const res = await fetch(`${apiBase}/api/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (res.ok) {
      const data = await res.json();
      const ratioText = compressed.isCompressed ? `（壓縮 ${compressed.ratio}）` : '';
      showMessage(`上傳成功 ${ratioText}`);
      await fetchMedia();
      return data?.media?.id ? `${apiBase}/api/media/${data.media.id}` : undefined;
    }

    if (res.status === 401) {
      await handleUnauthorized();
      throw new Error('Unauthorized');
    }

    let errorText = '';
    try {
      const errData = await res.json();
      errorText = errData.error || `HTTP ${res.status}`;
    } catch {
      errorText = `HTTP ${res.status}`;
    }
    showMessage(`上傳失敗：${errorText}`, 'error');
    throw new Error(errorText);
  };

  const handleDeleteMedia = async (id: string): Promise<void> => {
    const res = await fetch(`${apiBase}/api/media/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
      await handleUnauthorized();
      throw new Error('Unauthorized');
    }
    if (!res.ok) {
      let errorText = '';
      try {
        const errData = await res.json();
        errorText = errData.error || `HTTP ${res.status}`;
      } catch {
        errorText = `HTTP ${res.status}`;
      }
      showMessage(`刪除失敗：${errorText}`, 'error');
      throw new Error(errorText);
    }
    showMessage('已刪除');
    await fetchMedia();
  };

  const openContentImagePicker = async (target: typeof contentImagePickerTarget) => {
    if (!token) return;
    await fetchMedia();
    setContentImagePickerTarget(target);
    setContentImagePickerOpen(true);
  };

  const handleContentImageSelect = (urls: string[]) => {
    const url = urls[0];
    if (!url) return;

    setDraft((prev) => {
      if (contentImagePickerTarget === 'logo') {
        return { ...prev, siteConfig: { ...prev.siteConfig, logo: url } };
      }
      if (contentImagePickerTarget === 'hero') {
        return { ...prev, hero: { ...prev.hero, backgroundImage: url } };
      }
      if (contentImagePickerTarget === 'about') {
        return { ...prev, about: { ...prev.about, image: url } };
      }
      if (contentImagePickerTarget && typeof contentImagePickerTarget === 'object' && contentImagePickerTarget.type === 'news') {
        const idx = contentImagePickerTarget.index;
        const updatedNews = [...prev.news];
        updatedNews[idx] = { ...updatedNews[idx], image: url };
        return { ...prev, news: updatedNews };
      }
      return prev;
    });

    setContentImagePickerOpen(false);
    setContentImagePickerTarget(null);
  };

  const updateNews = (index: number, item: NewsItem) => {
    const updated = [...draft.news];
    updated[index] = item;
    setDraft({ ...draft, news: updated });
  };

  const addNews = () => {
    setDraft({
      ...draft,
      news: [
        ...draft.news,
        { id: `n${Date.now()}`, title: '', date: new Date().toISOString().slice(0, 10), summary: '', image: '', category: '' },
      ],
    });
  };

  const removeNews = (index: number) => {
    setDraft({ ...draft, news: draft.news.filter((_, i) => i !== index) });
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <form onSubmit={handleLogin} className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg">
          <h1 className="text-2xl font-bold text-slate-900 mb-6 text-center">Marveland 後台管理</h1>
          {loginError && <p className="mb-4 text-sm text-red-600">{loginError}</p>}
          <div className="mb-4">
            <label className="mb-1 block text-sm font-semibold text-slate-700">用戶名</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2"
            />
          </div>
          <div className="mb-6">
            <label className="mb-1 block text-sm font-semibold text-slate-700">密碼</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2"
            />
          </div>
          <button type="submit" className="w-full bg-gold-600 text-white py-3 rounded font-bold hover:bg-gold-700">
            登入
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {message && (
        <div className="fixed left-1/2 top-4 z-[70] -translate-x-1/2 transform">
          <div
            className={`rounded-lg px-6 py-3 text-sm font-bold shadow-lg ${
              messageType === 'error'
                ? 'bg-red-600 text-white'
                : 'bg-green-600 text-white'
            }`}
          >
            {message}
          </div>
        </div>
      )}
      <header className="bg-slate-900 text-white p-4 flex flex-wrap items-center justify-between gap-y-3">
        <div className="flex items-center gap-3 md:gap-4">
          <h1 className="text-lg font-bold whitespace-nowrap">Marveland 後台管理</h1>
          {currentUsername && (
            <span className="rounded bg-white/10 px-2 py-1 text-xs">
              {currentUsername} · {role === 'admin' ? 'Admin' : 'Staff'}
            </span>
          )}
        </div>
        <div className="flex max-w-full min-w-0 items-center gap-2 overflow-x-auto md:gap-4 [&>a]:shrink-0 [&>button]:shrink-0 [&>button]:whitespace-nowrap [&>a]:whitespace-nowrap">
          <button
            onClick={toggleAdminDark}
            aria-label={adminDark ? '切換至淺色模式' : '切換至深色模式'}
            title={adminDark ? '切換至淺色模式' : '切換至深色模式'}
            className="p-2 text-slate-300 hover:text-white transition-colors"
          >
            {adminDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            onClick={() => setPasswordModalOpen(true)}
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"
          >
            <KeyRound className="h-4 w-4" />
            修改密碼
          </button>
          <button
            onClick={() => saveDraft()}
            disabled={saving || !isDirty}
            className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-bold disabled:opacity-60 ${
              isDirty
                ? 'bg-gold-600 hover:bg-gold-700 text-white'
                : 'bg-slate-600 text-slate-200 cursor-default'
            }`}
          >
            <Save className="h-4 w-4" />
            {saving ? '儲存中...' : isDirty ? '儲存變更' : '已同步'}
          </button>
          <a
            href="/manual"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded border border-slate-500 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-white/10"
          >
            ？操作手冊
          </a>
          <button onClick={logout} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
            <LogOut className="h-4 w-4" />
            登出
          </button>
        </div>
      </header>

      <div className="flex flex-col md:flex-row">
        {/* 手機：橫向捲動分頁列；桌面：固定側欄 */}
        <aside className="w-full bg-white border-b md:border-b-0 md:border-r border-slate-200 md:min-h-screen md:w-48 flex overflow-x-auto md:block">
          {isAdmin && (
            <>
              {(['content', 'properties', 'team', 'news', 'inquiries', 'media', 'analytics', 'audit', 'handover'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`shrink-0 whitespace-nowrap px-4 py-3 text-left text-sm font-medium md:w-full ${
                    activeTab === tab ? 'bg-gold-50 text-gold-700' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {tab === 'content' && '內容編輯'}
                  {tab === 'properties' && '物業管理'}
                  {tab === 'team' && '團隊帳號'}
                  {tab === 'news' && '最新消息'}
                  {tab === 'inquiries' && '查詢記錄'}
                  {tab === 'media' && '媒體庫'}
                  {tab === 'analytics' && '數據分析'}
                  {tab === 'audit' && '操作日誌'}
                  {tab === 'handover' && '交接收據'}
                </button>
              ))}
            </>
          )}
          {!isAdmin && (
            <button
              onClick={() => setActiveTab('properties')}
              className={`shrink-0 whitespace-nowrap px-4 py-3 text-left text-sm font-medium md:w-full ${
                activeTab === 'properties' ? 'bg-gold-50 text-gold-700' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              物業管理
            </button>
          )}
        </aside>

        <main className="flex-1 min-w-0 p-4 md:p-8">
          {activeTab === 'content' && (
            <div className="space-y-8 max-w-3xl">
              <section className="bg-white p-6 rounded-xl shadow-sm">
                <h2 className="text-lg font-bold mb-4">網站基本資料</h2>
                <TextInput label="公司中文名稱" value={draft.siteConfig.name} onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, name: v } })} />
                <TextInput label="公司英文名稱" value={draft.siteConfig.nameEnglish} onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, nameEnglish: v } })} />
                <TextInput label="標語" value={draft.siteConfig.tagline} onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, tagline: v } })} />
                <ImageInput
                  label="Logo 路徑"
                  value={draft.siteConfig.logo}
                  onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, logo: v } })}
                  onPick={() => openContentImagePicker('logo')}
                />
                <h3 className="text-sm font-bold text-slate-700 mt-6 mb-3">社交媒體連結</h3>
                <TextInput label="Facebook" value={draft.siteConfig.social.facebook || ''} onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, social: { ...draft.siteConfig.social, facebook: v } } })} />
                <TextInput label="Instagram" value={draft.siteConfig.social.instagram || ''} onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, social: { ...draft.siteConfig.social, instagram: v } } })} />
                <TextInput label="WhatsApp 1 連結" value={draft.siteConfig.social.whatsapp1 || ''} onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, social: { ...draft.siteConfig.social, whatsapp1: v } } })} />
                <TextInput label="WhatsApp 2 連結" value={draft.siteConfig.social.whatsapp2 || ''} onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, social: { ...draft.siteConfig.social, whatsapp2: v } } })} />
                <TextInput label="WeChat" value={draft.siteConfig.social.wechat || ''} onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, social: { ...draft.siteConfig.social, wechat: v } } })} />

                <h3 className="text-sm font-bold text-slate-700 mt-6 mb-1">Google 廣告追蹤（可選）</h3>
                <p className="mb-3 text-xs text-slate-500">
                  填 GTM 容器 ID 即可由 GTM 統一管理全部代碼（其他欄位留空）；無 GTM 時填 Google Ads ID 直接追蹤。
                  WhatsApp 轉化 Label 令「點擊 WhatsApp」計入 Google Ads 轉化。填好後按上方「儲存變更」，約 1 分鐘全站生效。
                </p>
                <TextInput label="GTM 容器 ID（GTM-XXXXXXX）" value={draft.siteConfig.googleTracking?.gtmId || ''} onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, googleTracking: { ...draft.siteConfig.googleTracking, gtmId: v.trim() } } })} />
                <TextInput label="Google Ads ID（AW-XXXXXXX）" value={draft.siteConfig.googleTracking?.adsId || ''} onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, googleTracking: { ...draft.siteConfig.googleTracking, adsId: v.trim() } } })} />
                <TextInput label="GA4 ID（G-XXXXXXX，可選）" value={draft.siteConfig.googleTracking?.ga4Id || ''} onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, googleTracking: { ...draft.siteConfig.googleTracking, ga4Id: v.trim() } } })} />
                <TextInput label="WhatsApp 轉化 Label（AW-XXXXXXX/XXXXXX，可選）" value={draft.siteConfig.googleTracking?.whatsappLabel || ''} onChange={(v) => setDraft({ ...draft, siteConfig: { ...draft.siteConfig, googleTracking: { ...draft.siteConfig.googleTracking, whatsappLabel: v.trim() } } })} />

                <h3 className="text-sm font-bold text-slate-700 mt-6 mb-1">社群推廣標籤（Hashtag 標籤庫）</h3>
                <p className="mb-3 text-xs text-slate-500">
                  供「物業編輯 → Facebook 推廣」點選；未設定時使用內建預設清單。標籤以逗號分隔（輸入後按 Enter 或點輸入框外即完成），自動補 # 與去重。
                </p>
                {(draft.siteConfig.socialPromotion?.hashtagGroups || []).map((g, gi) => (
                  <div key={gi} className="mb-2 flex items-center gap-2">
                    <input
                      type="text"
                      value={g.name}
                      onChange={(e) => {
                        const groups = [...(draft.siteConfig.socialPromotion?.hashtagGroups || [])];
                        groups[gi] = { ...groups[gi], name: e.target.value };
                        setDraft({ ...draft, siteConfig: { ...draft.siteConfig, socialPromotion: { hashtagGroups: groups } } });
                      }}
                      placeholder="組名（如 地區）"
                      className="w-28 shrink-0 rounded border border-slate-300 px-2 py-1.5 text-sm"
                    />
                    <input
                      type="text"
                      value={tagInputDrafts[gi] ?? g.tags.join(', ')}
                      onChange={(e) => setTagInputDrafts((prev) => ({ ...prev, [gi]: e.target.value }))}
                      onBlur={() => commitTagDraft(gi)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitTagDraft(gi);
                        }
                      }}
                      placeholder="#荃灣, #葵芳, #地鐵（以逗號分隔，輸入後按 Enter 完成）"
                      className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const groups = (draft.siteConfig.socialPromotion?.hashtagGroups || []).filter((_, i) => i !== gi);
                        setDraft({ ...draft, siteConfig: { ...draft.siteConfig, socialPromotion: { hashtagGroups: groups } } });
                      }}
                      className="shrink-0 rounded border border-red-200 px-2 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                    >
                      刪除
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const groups = [...(draft.siteConfig.socialPromotion?.hashtagGroups || []), { name: '新分組', tags: [] }];
                    setDraft({ ...draft, siteConfig: { ...draft.siteConfig, socialPromotion: { hashtagGroups: groups } } });
                  }}
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                >
                  ＋ 新增分組
                </button>

                <h3 className="text-sm font-bold text-slate-700 mt-6 mb-1">防盜水印濃度</h3>
                <p className="mb-3 text-xs text-slate-500">
                  套用於之後新上傳的照片（已上傳的不受影響，需以原圖重傳）。0%＝不加水印；建議 10–25%。照片偏暗可調高、偏亮偏空可調低。
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={60}
                    step={2}
                    value={Math.round(watermarkOpacity * 100)}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        siteConfig: { ...draft.siteConfig, watermark: { opacity: Number(e.target.value) / 100 } },
                      })
                    }
                    className="w-64 accent-gold-600"
                  />
                  <span className="text-sm font-bold text-slate-700">{Math.round(watermarkOpacity * 100)}%</span>
                </div>
                <canvas ref={wmPreviewRef} width={300} height={140} className="mt-3 max-w-full rounded-lg border border-slate-200" />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <label className="cursor-pointer rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50">
                    用自己的照片預覽
                    <input type="file" accept="image/*" className="hidden" onChange={handleWmSamplePick} />
                  </label>
                  <button
                    type="button"
                    onClick={handleWmSampleReset}
                    className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                  >
                    重設樣本
                  </button>
                  <span className="text-xs text-slate-400">預覽僅在本機顯示，不會上傳或儲存照片。</span>
                </div>

                <h3 className="text-sm font-bold text-slate-700 mt-6 mb-1">FB 短連結體檢</h3>
                <p className="mb-3 text-xs text-slate-500">
                  掃描全部 FB 貼文短連結，列出「指向已下載樓盤」的死連結——客人㩒這些連結會看不到樓盤。請把死連結對應的 FB 貼文刪除或編輯。
                </p>
                <button
                  type="button"
                  onClick={async () => {
                    setAuditLoading(true);
                    setAuditResult(null);
                    try {
                      const res = await fetch(`${apiBase}/api/admin/shortlink-audit`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      const data = await res.json().catch(() => null);
                      if (!res.ok || !data?.success) {
                        showMessage(`體檢失敗：${data?.error || `HTTP ${res.status}`}`, 'error');
                      } else {
                        setAuditResult(data);
                      }
                    } catch (err) {
                      showMessage(`體檢失敗：${err instanceof Error ? err.message : String(err)}`, 'error');
                    }
                    setAuditLoading(false);
                  }}
                  disabled={auditLoading}
                  className="flex items-center gap-2 rounded bg-blue-800 px-4 py-2 text-xs font-bold text-white hover:bg-blue-900 disabled:opacity-50"
                >
                  {auditLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  掃描全部短連結
                </button>
                {auditResult && (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
                    <p className="font-semibold text-slate-700">
                      共 {auditResult.total} 條短連結：存活 {auditResult.aliveCount}｜
                      <span className={auditResult.deadCount > 0 ? 'text-red-600' : 'text-green-700'}>死連結 {auditResult.deadCount}</span>
                    </p>
                    {auditResult.deadCount > 0 && (
                      <div className="mt-2 max-h-64 space-y-1.5 overflow-y-auto">
                        {auditResult.dead.map((d: { code: string; shortUrl: string; propertyId: string; clicks: number }) => (
                          <div key={d.code} className="rounded border border-red-100 bg-white p-2">
                            <p className="font-mono text-[11px] text-slate-700">
                              /s/{d.code} → {d.propertyId}（{d.clicks} 次點擊）
                            </p>
                            <p className="break-all text-[11px] text-slate-400">{d.shortUrl}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </section>

              <section className="bg-white p-6 rounded-xl shadow-sm">
                <h2 className="text-lg font-bold mb-4">主海報 Hero</h2>
                <ImageInput
                  label="背景圖片 URL"
                  value={draft.hero.backgroundImage}
                  onChange={(v) => setDraft({ ...draft, hero: { ...draft.hero, backgroundImage: v } })}
                  onPick={() => openContentImagePicker('hero')}
                />
                <TextInput label="標語" value={draft.hero.tagline} onChange={(v) => setDraft({ ...draft, hero: { ...draft.hero, tagline: v } })} />
                <TextInput label="標題第一行" value={draft.hero.titleLine1} onChange={(v) => setDraft({ ...draft, hero: { ...draft.hero, titleLine1: v } })} />
                <TextInput label="標題第二行" value={draft.hero.titleLine2} onChange={(v) => setDraft({ ...draft, hero: { ...draft.hero, titleLine2: v } })} />
                <TextArea label="描述" value={draft.hero.description} onChange={(v) => setDraft({ ...draft, hero: { ...draft.hero, description: v } })} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextInput label="主按鈕文字" value={draft.hero.primaryButtonText} onChange={(v) => setDraft({ ...draft, hero: { ...draft.hero, primaryButtonText: v } })} />
                  <TextInput label="主按鈕連結" value={draft.hero.primaryButtonLink} onChange={(v) => setDraft({ ...draft, hero: { ...draft.hero, primaryButtonLink: v } })} />
                  <TextInput label="次按鈕文字" value={draft.hero.secondaryButtonText} onChange={(v) => setDraft({ ...draft, hero: { ...draft.hero, secondaryButtonText: v } })} />
                  <TextInput label="次按鈕連結" value={draft.hero.secondaryButtonLink} onChange={(v) => setDraft({ ...draft, hero: { ...draft.hero, secondaryButtonLink: v } })} />
                </div>
              </section>

              <section className="bg-white p-6 rounded-xl shadow-sm">
                <h2 className="text-lg font-bold mb-4">關於我們</h2>
                <TextInput label="區塊標題" value={draft.about.sectionTitle} onChange={(v) => setDraft({ ...draft, about: { ...draft.about, sectionTitle: v } })} />
                <TextArea label="公司簡介" value={draft.about.description} onChange={(v) => setDraft({ ...draft, about: { ...draft.about, description: v } })} />
                <ImageInput
                  label="圖片 URL"
                  value={draft.about.image}
                  onChange={(v) => setDraft({ ...draft, about: { ...draft.about, image: v } })}
                  onPick={() => openContentImagePicker('about')}
                />
              </section>

              <section className="bg-white p-6 rounded-xl shadow-sm">
                <h2 className="text-lg font-bold mb-4">聯絡資料</h2>
                <TextInput label="電話" value={draft.contact.info.phoneDisplay} onChange={(v) => setDraft({ ...draft, contact: { ...draft.contact, info: { ...draft.contact.info, phoneDisplay: v } } })} />
                <TextInput label="電郵" value={draft.contact.info.email} onChange={(v) => setDraft({ ...draft, contact: { ...draft.contact, info: { ...draft.contact.info, email: v } } })} />
                <TextInput label="服務時間" value={draft.contact.info.businessHours} onChange={(v) => setDraft({ ...draft, contact: { ...draft.contact, info: { ...draft.contact.info, businessHours: v } } })} />
                <TextArea label="地址" value={draft.contact.info.addressDisplay} onChange={(v) => setDraft({ ...draft, contact: { ...draft.contact, info: { ...draft.contact.info, addressDisplay: v } } })} />
              </section>

              <section className="bg-white p-6 rounded-xl shadow-sm">
                <h2 className="text-lg font-bold mb-4">頁腳</h2>
                <TextInput label="牌照號碼" value={draft.footer.licenseNumber} onChange={(v) => setDraft({ ...draft, footer: { ...draft.footer, licenseNumber: v } })} />
                <TextArea label="版權文字" value={draft.footer.copyright} onChange={(v) => setDraft({ ...draft, footer: { ...draft.footer, copyright: v } })} />
              </section>
            </div>
          )}

          {activeTab === 'properties' && (
            <PropertyManager
              draft={draft}
              setDraft={setDraft}
              saveDraft={saveDraft}
              token={token || ''}
              apiBase={apiBase}
              media={media}
              fetchMedia={fetchMedia}
              showMessage={showMessage}
              onAudit={audit}
              currentUsername={currentUsername}
              role={role || undefined}
              usedMediaIds={usedMediaIds}
              onDeleteMedia={handleDeleteMedia}
              watermarkOpacity={watermarkOpacity}
            />
          )}

          {activeTab === 'team' && isAdmin && (
            <TeamManager
              draft={draft}
              setDraft={setDraft}
              saveDraft={saveDraft}
              apiBase={apiBase}
              token={token || ''}
              currentUsername={currentUsername}
              currentUserId={currentUserId || ''}
              showMessage={showMessage}
              onAudit={audit}
              media={media}
              fetchMedia={fetchMedia}
              onUploadFile={handleUploadFile}
            />
          )}

          {activeTab === 'news' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">最新消息</h2>
                <button onClick={addNews} className="flex items-center gap-2 rounded bg-blue-800 px-4 py-2 text-sm font-bold text-white hover:bg-blue-900">
                  <Plus className="h-4 w-4" />
                  新增消息
                </button>
              </div>
              <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 text-left">標題</th>
                      <th className="px-4 py-3 text-left w-32">日期</th>
                      <th className="px-4 py-3 text-left w-28">分類</th>
                      <th className="px-4 py-3 text-left">圖片 URL</th>
                      <th className="px-4 py-3 text-left w-64">摘要</th>
                      <th className="px-4 py-3 text-left">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {draft.news.map((item, idx) => (
                      <tr key={item.id} className="border-t border-slate-100">
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={item.title}
                            onChange={(e) => updateNews(idx, { ...item, title: e.target.value })}
                            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-gold-600 focus:outline-none"
                            placeholder="標題"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="date"
                            value={item.date}
                            onChange={(e) => updateNews(idx, { ...item, date: e.target.value })}
                            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-gold-600 focus:outline-none"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={item.category}
                            onChange={(e) => updateNews(idx, { ...item, category: e.target.value })}
                            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-gold-600 focus:outline-none"
                            placeholder="分類"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            {item.image && (
                              <img src={item.image} alt="" className="h-8 w-8 rounded object-cover" />
                            )}
                            <input
                              type="text"
                              value={item.image}
                              onChange={(e) => updateNews(idx, { ...item, image: e.target.value })}
                              className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-gold-600 focus:outline-none"
                              placeholder="https://..."
                            />
                            <button
                              type="button"
                              onClick={() => openContentImagePicker({ type: 'news', index: idx })}
                              className="rounded bg-blue-800 p-1.5 text-white hover:bg-blue-900"
                              title="從媒體庫選擇"
                            >
                              <ImageIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <textarea
                            value={item.summary}
                            onChange={(e) => updateNews(idx, { ...item, summary: e.target.value })}
                            rows={2}
                            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-gold-600 focus:outline-none"
                            placeholder="摘要"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => removeNews(idx)}
                            className="rounded bg-red-100 p-1.5 text-red-600 hover:bg-red-200"
                            title="刪除"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'inquiries' && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-3 text-left">類型</th>
                    <th className="px-4 py-3 text-left">姓名</th>
                    <th className="px-4 py-3 text-left">電話</th>
                    <th className="px-4 py-3 text-left">地址</th>
                    <th className="px-4 py-3 text-left">時間</th>
                    <th className="px-4 py-3 text-left">狀態</th>
                  </tr>
                </thead>
                <tbody>
                  {inquiries.map((inq) => (
                    <tr key={inq.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">{inq.type}</td>
                      <td className="px-4 py-3">{inq.name}</td>
                      <td className="px-4 py-3">{inq.phone}</td>
                      <td className="px-4 py-3">{inq.address}</td>
                      <td className="px-4 py-3">{new Date(inq.createdAt).toLocaleString('zh-HK')}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${inq.status === 'new' ? 'bg-red-100 text-red-700' : inq.status === 'contacted' ? 'bg-gold-100 text-gold-700' : 'bg-green-100 text-green-700'}`}>
                          {inq.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'handover' && isAdmin && (
            <HandoverPanel apiBase={apiBase} token={token} />
          )}
          {activeTab === 'analytics' && isAdmin && (
            <AnalyticsDashboard token={token || ''} apiBase={apiBase} />
          )}

          {activeTab === 'audit' && isAdmin && (
            <AuditLog apiBase={apiBase} token={token || ''} showMessage={showMessage} />
          )}

          {activeTab === 'media' && isAdmin && (
            <MediaLibrary
              apiBase={apiBase}
              token={token || ''}
              media={media}
              usage={mediaUsage}
              fetchMedia={fetchMedia}
              showMessage={showMessage}
              watermarkOpacity={watermarkOpacity}
            />
          )}
        </main>

        <MediaPicker
          isOpen={contentImagePickerOpen}
          onClose={() => {
            setContentImagePickerOpen(false);
            setContentImagePickerTarget(null);
          }}
          media={media}
          apiBase={apiBase}
          mode="single"
          onSelect={handleContentImageSelect}
          onUpload={handleUploadFile}
          onDelete={handleDeleteMedia}
          usedMediaIds={usedMediaIds}
        />
        {passwordModalOpen && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
              <h3 className="mb-4 text-lg font-bold text-slate-900">修改密碼</h3>
              <form onSubmit={handleChangeOwnPassword} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">目前密碼</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">新密碼</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setPasswordModalOpen(false);
                      setCurrentPassword('');
                      setNewPassword('');
                    }}
                    className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={!currentPassword || !newPassword}
                    className="rounded-lg bg-blue-800 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-900 disabled:opacity-50"
                  >
                    更新密碼
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
